import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import Sparkline from "./Sparkline";
import StatusPill from "./StatusPill";
import { fmtAUD, fmtPct, flagFor } from "../lib/format";

export default function RouteCard({ route, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.03, 0.6) }}
      data-testid={`route-card-${route.route_id}`}
    >
      <Link to={`/routes/${route.route_id}`} className="block">
        <div className="saa-card route-card-glow p-5 h-full" data-status={route.status}>
          {/* Top row */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl leading-none" title={route.origin_city}>
                {flagFor(route.origin_cc)}
              </span>
              <ArrowRight size={14} weight="bold" style={{ color: "var(--text-muted)" }} />
              <span className="text-2xl leading-none" title={route.destination_city}>
                {flagFor(route.destination_cc)}
              </span>
            </div>
            <StatusPill status={route.status} testid={`status-${route.route_id}`} />
          </div>

          {/* Title */}
          <div className="mb-1">
            <div className="font-display text-lg font-semibold leading-tight">
              {route.origin} <span style={{ color: "var(--text-muted)" }}>→</span> {route.destination}
            </div>
            <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
              {route.origin_city} → {route.destination_city}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
                SAA avg
              </div>
              <div className="font-mono font-semibold text-base mt-0.5"
                   style={{ color: route.saa_avg ? "var(--saa-gold)" : "var(--text-muted)" }}>
                {route.saa_avg ? fmtAUD(route.saa_avg) : "Awaiting"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>
                Cheapest comp.
              </div>
              <div className="font-mono font-semibold text-base mt-0.5"
                   style={{ color: route.cheapest_competitor ? "var(--text-primary)" : "var(--text-muted)" }}>
                {route.cheapest_competitor ? fmtAUD(route.cheapest_competitor) : "Awaiting"}
              </div>
            </div>
          </div>

          {/* Gap */}
          {route.gap_pct != null && (
            <div className="mt-3 text-xs flex items-center gap-2">
              <span style={{ color: "var(--text-secondary)" }}>Gap to SAA</span>
              <span
                className="font-mono font-semibold"
                style={{
                  color:
                    route.gap_pct > 15
                      ? "var(--danger)"
                      : route.gap_pct > 0
                      ? "var(--warning)"
                      : "var(--success)",
                }}
              >
                {fmtPct(route.gap_pct)}
              </span>
            </div>
          )}

          {/* Sparkline */}
          <div className="mt-4 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <div className="text-[10px] uppercase tracking-[0.16em] mb-1" style={{ color: "var(--text-muted)" }}>
              SAA monthly trend
            </div>
            <Sparkline data={route.sparkline} />
          </div>

          {/* Footer */}
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-1 text-[10px]" style={{ color: "var(--text-secondary)" }}>
              {route.is_verified && (
                <>
                  <ShieldCheck size={11} weight="fill" color="#10B981" />
                  <span>Verified</span>
                </>
              )}
              {!route.is_verified && <span>Unverified</span>}
            </div>
            <div className="text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>
              {route.competitors.length} competitors
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
